<template>
  <div class="view-container">
    <div class="search-panel">
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-user"></i> 账号</label>
          <input v-model="searchForm.account" type="text" placeholder="请输入账号" class="search-input">
        </div>
        <div class="search-item">
          <label><i class="fas fa-id-card"></i> 姓名</label>
          <input v-model="searchForm.name" type="text" placeholder="请输入姓名" class="search-input">
        </div>
        <div class="search-item">
          <label><i class="fas fa-tag"></i> 用户类型</label>
          <select v-model="searchForm.userType" class="search-select">
            <option value="">全部</option>
            <option value="管理员">管理员</option>
            <option value="普通用户">普通用户</option>
            <option value="供应商">供应商</option>
            <option value="客户">客户</option>
          </select>
        </div>
        <div class="search-item">
          <label><i class="fas fa-toggle-on"></i> 登录状态</label>
          <select v-model="searchForm.loginStatus" class="search-select">
            <option value="">全部</option>
            <option value="valid">有效可登录</option>
            <option value="no-login">有效不可登录</option>
            <option value="disabled">失效</option>
          </select>
        </div>
        <div class="search-actions">
          <button @click="handleSearch" class="btn btn-primary search-btn">
            <i class="fas fa-search"></i> 查询
          </button>
          <button @click="resetSearch" class="btn btn-default search-btn">
            <i class="fas fa-undo"></i> 重置
          </button>
          <button @click="handleExport" class="btn btn-success search-btn" :disabled="exporting">
            <i class="fas fa-download"></i> {{ exporting ? '导出中...' : '导出' }}
          </button>
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <div class="table-header-row">
        <button @click="openAddModal" class="btn btn-primary btn-sm">
          <i class="fas fa-plus"></i> 新增用户
        </button>
        <button @click="toggleColumnSelector" class="btn btn-default btn-sm column-settings-btn">
          <i class="fas fa-columns"></i> 列设置
        </button>
      </div>
      
      <div class="table-container">
        <table class="data-table" @mousedown="startColumnDrag" @mousemove="onColumnDrag" @mouseup="endColumnDrag">
          <thead>
            <tr>
              <th 
                v-for="column in visibleColumns" 
                :key="column.key"
                :class="{ 'sortable': column.sortable, 'sorted-asc': sortColumn === column.key && sortOrder === 'asc', 'sorted-desc': sortColumn === column.key && sortOrder === 'desc' }"
                @click="handleSort(column.key)"
                :draggable="true"
                @dragstart="dragStart($event, column)"
                @dragover.prevent
                @drop="dropColumn($event, column)"
                class="column-header"
                :style="column.width ? { width: column.width, maxWidth: column.width, minWidth: column.width } : {}"
              >
                <span>{{ column.label }}</span>
                <i v-if="column.sortable" class="fas" :class="sortColumn === column.key ? (sortOrder === 'asc' ? 'fa-chevron-up' : 'fa-chevron-down') : 'fa-sort'"></i>
              </th>
              <th class="action-column">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in filteredUsers" :key="user.id">
              <td v-for="column in visibleColumns" :key="column.key" :style="column.width ? { width: column.width, maxWidth: column.width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}">
                <template v-if="column.key === 'loginStatus'">
                  <span :class="['status-badge', user.loginStatusClass]">
                    {{ user.loginStatusText }}
                  </span>
                </template>
                <template v-else-if="column.key === 'organization'">
                  {{ user.organization || '-' }}
                </template>
                <template v-else-if="column.key === 'roles'">
                  {{ user.roles || '-' }}
                </template>
                <template v-else>
                  {{ user[column.key] || '-' }}
                </template>
              </td>
              <td class="action-column">
                <button @click="viewUser(user)" class="btn btn-info btn-xs" title="查看详情">
                  <i class="fas fa-eye"></i>
                </button>
                <button v-if="user.active" @click="editUser(user)" class="btn btn-primary btn-xs" title="修改">
                  <i class="fas fa-edit"></i>
                </button>
                <button v-if="user.active" @click="resetPassword(user)" class="btn btn-warning btn-xs" title="修改密码">
                  <i class="fas fa-key"></i>
                </button>
                <button v-if="user.active" @click="disableUserHandle(user)" class="btn btn-danger btn-xs" title="失效">
                  <i class="fas fa-ban"></i>
                </button>
                <button v-else @click="enableUserHandle(user)" class="btn btn-success btn-xs" title="启用">
                  <i class="fas fa-check-circle"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-row">
        <div class="pagination-info">
          共 {{ totalUsers }} 条记录
        </div>
        <div class="pagination-controls">
          <div class="page-size-selector">
            <span>每页</span>
            <select v-model="pageSize" class="page-size-input form-control">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
            <span>条</span>
          </div>
          <button @click="prevPage" :disabled="currentPage === 1" class="btn btn-default btn-sm pagination-btn">
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="page-numbers">
            <button 
              v-for="page in visiblePages" 
              :key="page"
              @click="goToPage(page)"
              :class="['page-btn', { active: currentPage === page, disabled: page === '...' }]"
              :disabled="page === '...'"
            >
              {{ page }}
            </button>
          </div>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="btn btn-default btn-sm pagination-btn">
            <i class="fas fa-chevron-right"></i>
          </button>
          <div class="page-jump">
            <span>跳转到</span>
            <input 
              type="number" 
              v-model="jumpPage" 
              min="1" 
              :max="totalPages"
              class="jump-input"
              @keyup.enter="handleJump"
            >
            <span>页</span>
            <button @click="handleJump" class="btn btn-primary btn-sm jump-btn">GO</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showColumnSelector" class="column-selector-modal" @click.self="toggleColumnSelector">
      <div class="column-selector-content">
        <div class="column-selector-header">
          <h4>列设置</h4>
          <button @click="toggleColumnSelector" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="column-selector-body">
          <label v-for="column in allColumns" :key="column.key" class="column-checkbox">
            <input type="checkbox" v-model="column.visible" @change="updateVisibleColumns">
            <span>{{ column.label }}</span>
          </label>
        </div>
        <div class="column-selector-footer">
          <button @click="resetColumns" class="btn btn-default">重置</button>
          <button @click="toggleColumnSelector" class="btn btn-primary">确定</button>
        </div>
      </div>
    </div>

    <div v-if="showAddModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-container">
        <div class="modal-header">
          <h3>{{ isView ? '查看用户' : editingUser ? '修改用户' : '新增用户' }}</h3>
          <button @click="closeModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveUser" class="form-horizontal">
            <div class="form-row">
              <div class="form-group">
                <label>账号 *</label>
                <input v-model="form.account" type="text" class="form-control" :disabled="!!editingUser || isView" required>
              </div>
              <div class="form-group">
                <label>姓名 *</label>
                <input v-model="form.name" type="text" class="form-control" :disabled="isView" required>
              </div>
              <div class="form-group">
                <label>性别</label>
                <select v-model="form.gender" class="form-control" :disabled="isView">
                  <option value="男">男</option>
                  <option value="女">女</option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>职位</label>
                <input v-model="form.position" type="text" class="form-control" :disabled="isView">
              </div>
              <div class="form-group">
                <label>邮箱</label>
                <input v-model="form.email" type="email" class="form-control" :disabled="isView">
              </div>
              <div class="form-group">
                <label>手机</label>
                <input v-model="form.phone" type="text" class="form-control" :disabled="isView">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>工号</label>
                <input v-model="form.employeeNo" type="text" class="form-control" :disabled="isView">
              </div>
              <div class="form-group">
                <label>直接领导</label>
                <div class="search-select">
                  <input v-model="form.leaderName" type="text" class="form-control" readonly @click="showLeaderSelector = true" :disabled="isView" placeholder="点击选择直接领导">
                  <button type="button" @click="showLeaderSelector = true" :disabled="isView" class="search-btn">
                    <i class="fas fa-search"></i>
                  </button>
                </div>
              </div>
              <div class="form-group">
                <label>用户类型</label>
                <select v-model="form.userType" class="form-control" :disabled="isView">
                  <option value="普通用户">普通用户</option>
                  <option value="供应商">供应商</option>
                  <option value="客户">客户</option>
                </select>
              </div>
            </div>
            <div class="form-row" v-if="!editingUser && !isView">
              <div class="form-group">
                <label>密码 *</label>
                <input v-model="form.password" type="password" class="form-control" required>
              </div>
              <div class="form-group">
                <label>确认密码 *</label>
                <input v-model="form.confirmPassword" type="password" class="form-control" required>
              </div>
            </div>
            <div class="form-row" v-if="!isView">
              <div class="form-group" style="flex:1">
                <label>所在组织</label>
                <div class="search-select">
                  <input v-model="form.organization" type="text" class="form-control" readonly @click="showOrgSelector = true">
                  <button type="button" @click="showOrgSelector = true" class="search-btn">
                    <i class="fas fa-search"></i>
                  </button>
                </div>
              </div>
              <div class="form-group" style="flex:1">
                <label>所在角色</label>
                <div class="search-select">
                  <input v-model="form.roleNames" type="text" class="form-control" readonly @click="showRoleSelector = true">
                  <button type="button" @click="showRoleSelector = true" class="search-btn">
                    <i class="fas fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="form-row" v-if="!isView">
              <div class="form-group" style="flex:1">
                <label>可登录账套</label>
                <div class="search-select">
                  <input v-model="tenantDisplayText" type="text" class="form-control" readonly @click="showTenantSelector = true" placeholder="点击选择账套">
                  <button type="button" @click="showTenantSelector = true" class="search-btn">
                    <i class="fas fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="form-row" v-if="isView">
              <div class="form-group" style="flex:1">
                <label>所在组织</label>
                <input v-model="form.organization" type="text" class="form-control" disabled>
              </div>
              <div class="form-group" style="flex:1">
                <label>所在角色</label>
                <input v-model="form.roleNames" type="text" class="form-control" disabled>
              </div>
            </div>
            <div class="form-row" v-if="isView && form.tenants && form.tenants.length">
              <div class="form-group" style="flex:1">
                <label>可用账套</label>
                <div style="display:flex;flex-wrap:wrap;gap:6px;padding-top:4px">
                  <span v-for="t in form.tenants" :key="t.tenantID || t.TenantID" class="tenant-tag">
                    {{ t.corpName || t.CorpName }}
                  </span>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="checkbox-label">
                  <input v-model="form.canLogin" type="checkbox" :disabled="isView"> 允许登录
                </label>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer" v-if="!isView">
          <button @click="closeModal" class="btn btn-default">取消</button>
          <button @click="saveUser" class="btn btn-primary">保存</button>
        </div>
        <div class="modal-footer" v-else>
          <button @click="closeModal" class="btn btn-default">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="showPasswordModal" class="modal-overlay" @click.self="closePasswordModal">
      <div class="modal-container">
        <div class="modal-header">
          <h3>修改密码</h3>
          <button @click="closePasswordModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="updatePassword" class="form-horizontal">
            <div class="form-row">
              <div class="form-group">
                <label>新密码 *</label>
                <input v-model="passwordForm.newPassword" type="password" class="form-control" required>
              </div>
              <div class="form-group">
                <label>确认密码 *</label>
                <input v-model="passwordForm.confirmPassword" type="password" class="form-control" required>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button @click="closePasswordModal" class="btn btn-default">取消</button>
          <button @click="updatePassword" class="btn btn-primary">确认修改</button>
        </div>
      </div>
    </div>

    <!-- 组织选择器 -->
    <TreeSelector
      :visible="showOrgSelector"
      title="选择组织"
      search-placeholder="搜索组织..."
      icon="fa-building"
      :nodes="orgTree"
      :selected-ids="selectedOrgIds"
      :multiple="true"
      @close="showOrgSelector = false"
      @confirm="onOrgSelected"
      @update:selected-ids="(ids) => selectedOrgIds = ids"
    />

    <!-- 角色选择器 -->
    <TreeSelector
      :visible="showRoleSelector"
      title="选择角色"
      search-placeholder="搜索角色..."
      icon="fa-user-tag"
      :nodes="roleTree"
      :selected-ids="selectedRoleIds"
      :multiple="true"
      @close="showRoleSelector = false"
      @confirm="confirmRoles"
      @update:selected-ids="(ids) => selectedRoleIds = ids"
    />

    <!-- 直接领导选择器 -->
    <PersonSelector
      :visible="showLeaderSelector"
      @close="showLeaderSelector = false"
      @selected="onLeaderSelected"
    />

    <div v-if="showTenantSelector" class="modal-overlay" @click.self="showTenantSelector = false">
      <div class="modal-container tenant-selector">
        <div class="modal-header">
          <h3>选择可登录账套</h3>
          <button @click="showTenantSelector = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="search-box">
            <input v-model="tenantSearch" type="text" class="form-control" placeholder="搜索账套">
          </div>
          <div class="role-list">
            <label class="select-all-row">
              <input type="checkbox" :checked="isAllTenantSelected" @change="toggleAllTenant">
              <span>全选</span>
            </label>
            <label v-for="t in filteredTenants" :key="t.id" class="role-checkbox">
              <input type="checkbox" :checked="selectedTenantIds.includes(t.id)" @change="toggleTenant(t.id)">
              <span>{{ t.name }} ({{ t.tenantID }})</span>
            </label>
            <div v-if="filteredTenants.length === 0" class="empty-tip">没有可用的账套</div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showTenantSelector = false" class="btn btn-default">取消</button>
          <button @click="confirmTenants" class="btn btn-primary">确认</button>
        </div>
      </div>
    </div>

    <!-- 确认弹窗 -->
    <ConfirmModal
      :visible="confirmVisible"
      :title="confirmTitle"
      :message="confirmMessage"
      :type="confirmType"
      :confirm-text="confirmText"
      :loading="confirmLoading"
      @confirm="handleConfirm"
      @cancel="handleConfirmCancel"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { getUserList, saveUser as saveUserApi, exportUsers, enableUser, disableUser, updateUserPassword } from '../api/user'
import { getOrgTree, addOrgUser, removeOrgUser } from '../api/org'
import { getRoleTree, addRoleUsers, removeRoleUser } from '../api/role'
import { getTenants } from '../api/auth'
import { getUser } from '../utils/sessionManager'
import ConfirmModal from '../components/ConfirmModal.vue'
import TreeSelector from '../components/TreeSelector.vue'
import PersonSelector from './develop/ProcessDesigner/PersonSelector.vue'

const showAddModal = ref(false)
const showPasswordModal = ref(false)
const showColumnSelector = ref(false)
const showOrgSelector = ref(false)
const showRoleSelector = ref(false)
const showTenantSelector = ref(false)
const showLeaderSelector = ref(false)
const editingUser = ref(null)
const isView = ref(false)
const selectedRoles = ref([])
const selectedRoleIds = ref([])
const selectedOrgIds = ref([])
const oldOrgIds = ref([])
const oldRoleIds = ref([])
const tenantSearch = ref('')
const selectedTenantIds = ref([])
const currentPage = ref(1)
const pageSize = ref(20)
const sortColumn = ref('account')
const sortOrder = ref('asc')
const jumpPage = ref(1)
const exporting = ref(false)

// 确认弹窗状态
const confirmVisible = ref(false)
const confirmTitle = ref('确认操作')
const confirmMessage = ref('确定要执行此操作吗？')
const confirmType = ref('primary')
const confirmText = ref('确认')
const confirmLoading = ref(false)
let pendingAction = null

const searchForm = reactive({
  account: '',
  name: '',
  userType: '',
  loginStatus: ''
})

const form = reactive({
  account: '',
  name: '',
  gender: '男',
  position: '',
  email: '',
  phone: '',
  employeeNo: '',
  leader: '',
  leaderName: '',
  userType: '普通用户',
  password: '',
  confirmPassword: '',
  organization: '',
  organizationId: '',
  roleNames: '',
  canLogin: true,
  tenants: []
})

const passwordForm = reactive({
  newPassword: '',
  confirmPassword: ''
})

const allColumns = ref([
  { key: 'account', label: '账号', sortable: true, visible: true, width: '55px' },
  { key: 'name', label: '姓名', sortable: true, visible: true, width: '45px' },
  { key: 'employeeNo', label: '工号', sortable: true, visible: true, width: '50px' },
  { key: 'gender', label: '性别', sortable: true, visible: true, width: '35px' },
  { key: 'position', label: '职位', sortable: true, visible: true, width: '45px' },
  { key: 'email', label: '邮箱', sortable: true, visible: true, width: '80px' },
  { key: 'phone', label: '手机', sortable: true, visible: true, width: '60px' },
  { key: 'userType', label: '类型', sortable: true, visible: true, width: '40px' },
  { key: 'roles', label: '角色', sortable: true, visible: true, width: '65px' },
  { key: 'organization', label: '组织', sortable: true, visible: true, width: '65px' },
  { key: 'loginStatus', label: '状态', sortable: true, visible: true, width: '50px' }
])

const users = ref([])

// 将后端 UserListRow 字段映射为视图所需结构
const mapUser = (u) => {
  const active = (u.active ?? u.Active) === 1
  const userStatus = u.userStatus ?? u.UserStatus ?? 0
  // 登录状态：Active=0 → 失效；Active=1 + UserStatus=0 → 有效可登录；Active=1 + UserStatus=1 → 有效不可登录
  let loginStatusText, loginStatusClass
  if (!active) {
    loginStatusText = '失效'
    loginStatusClass = 'disabled'
  } else if (userStatus === 1) {
    loginStatusText = '有效不可登录'
    loginStatusClass = 'no-login'
  } else {
    loginStatusText = '有效可登录'
    loginStatusClass = 'valid'
  }

  return {
    id: u.guid || u.Guid,
    guid: u.guid || u.Guid,
    account: u.account || u.Account,
    name: u.displayName || u.DisplayName,
    gender: u.sexText || u.SexText || ((u.sex ?? u.Sex) === 1 ? '男' : '女'),
    position: u.post || u.Post || '',
    email: u.email || u.Email || '',
    phone: u.mb || u.Mb || u.tel || u.Tel || '',
    employeeNo: u.hRID || u.HRID || u.hrid || '',
    leader: u.superManage || u.SuperManage || '',
    leaderName: u.superManageName || u.SuperManageName || u.superManage || u.SuperManage || '',
    userType: u.userType || u.UserType || '普通用户',
    roles: u.roles || u.Roles || '',
    organization: u.deptName || u.DeptName || '',
    loginStatusText,
    loginStatusClass,
    active,
    userStatus,
    tenants: u.tenants || u.Tenants || [],
    _raw: u
  }
}

const loadUsers = async () => {
  try {
    const res = await getUserList()
    const list = res.data || res.Data || []
    users.value = list.map(mapUser)
    // 诊断：打印第一个用户原始数据的 hrid 字段
    if (list.length > 0) {
      const first = list[0]
      console.log('[Users] loadUsers 第一条原始记录:', JSON.stringify({ account: first.account||first.Account, name: first.displayName||first.DisplayName, hrid: first.hrid||first.HRID }))
    }
  } catch (e) {
    console.error('加载用户列表失败', e)
  }
}

const handleExport = async () => {
  exporting.value = true
  try {
    const res = await exportUsers()
    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = '用户列表.xlsx'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  } catch (e) {
    console.error('导出失败', e)
    $message.error('导出失败，请稍后重试')
  } finally {
    exporting.value = false
  }
}

onMounted(() => {
  loadUsers()
  loadOrgs()
  loadRoles()
  loadTenants()
})

const organizations = ref([])
const orgTree = ref([])
const roles = ref([])
const roleTree = ref([])
const allTenants = ref([])

const loadOrgs = async () => {
  try {
    const res = await getOrgTree()
    const list = res.data || res.Data || []
    orgTree.value = list.map(o => mapOrgNode(o))
    const flat = (nodes) => {
      let result = []
      for (const node of nodes) {
        result.push({ id: node.id, name: node.name, parentId: node.parentId })
        if (node.children && node.children.length > 0) result = result.concat(flat(node.children))
      }
      return result
    }
    organizations.value = flat(orgTree.value)
  } catch (e) {
    console.error('加载组织列表失败', e)
  }
}

const mapOrgNode = (n) => ({
  id: n.guid || n.Guid,
  name: n.ousName || n.oUsName || n.OUsName || n.name || n.Name,
  parentId: n.superGuid || n.SuperGuid,
  no: n.no ?? n.No ?? 0,
  children: (n.children || n.Children || []).map(mapOrgNode)
})

const mapRoleNode = (n) => ({
  id: n.guid || n.Guid,
  name: n.roleName || n.RoleName,
  no: n.no ?? n.No ?? 0,
  children: (n.children || n.Children || []).map(mapRoleNode)
})

const loadRoles = async () => {
  try {
    const res = await getRoleTree()
    const list = res.data || res.Data || []
    roleTree.value = list.map(r => mapRoleNode(r))
    const flat = (nodes) => {
      let result = []
      for (const node of nodes) {
        result.push({ id: node.id, name: node.name })
        if (node.children && node.children.length > 0) result = result.concat(flat(node.children))
      }
      return result
    }
    roles.value = flat(roleTree.value)
  } catch (e) {
    console.error('加载角色列表失败', e)
  }
}

const loadTenants = async () => {
  try {
    const currentUser = getUser()
    const account = currentUser?.account || currentUser?.Account || ''
    const res = await getTenants(account)
    const list = res.data || res.Data || []
    allTenants.value = list.map(t => ({
      id: t.id || t.ID || t.guid || t.Guid,
      name: t.name || t.Name || t.corpName || t.CorpName,
      tenantID: t.tenantID || t.TenantID || t.tenantId || t.TenantId
    }))
  } catch (e) {
    console.error('加载账套列表失败', e)
  }
}

const visibleColumns = computed(() => allColumns.value.filter(col => col.visible))

const filteredUsers = computed(() => {
  let result = [...users.value]
  
  if (searchForm.account) {
    result = result.filter(u => u.account.toLowerCase().includes(searchForm.account.toLowerCase()))
  }
  if (searchForm.name) {
    result = result.filter(u => u.name.toLowerCase().includes(searchForm.name.toLowerCase()))
  }
  if (searchForm.userType) {
    result = result.filter(u => u.userType === searchForm.userType)
  }
  if (searchForm.loginStatus) {
    result = result.filter(u => u.loginStatusClass === searchForm.loginStatus)
  }
  
  result.sort((a, b) => {
    const aVal = a[sortColumn.value]
    const bVal = b[sortColumn.value]
    if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1
    return 0
  })
  
  const start = (currentPage.value - 1) * pageSize.value
  return result.slice(start, start + pageSize.value)
})

const totalUsers = computed(() => {
  let result = [...users.value]
  if (searchForm.account) result = result.filter(u => u.account.toLowerCase().includes(searchForm.account.toLowerCase()))
  if (searchForm.name) result = result.filter(u => u.name.toLowerCase().includes(searchForm.name.toLowerCase()))
  if (searchForm.userType) result = result.filter(u => u.userType === searchForm.userType)
  if (searchForm.loginStatus) result = result.filter(u => u.loginStatusClass === searchForm.loginStatus)
  return result.length
})

const totalPages = computed(() => Math.ceil(totalUsers.value / pageSize.value))

const visiblePages = computed(() => {
  const pages = []
  const total = totalPages.value
  const current = currentPage.value
  
  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    pages.push(1)
    if (current > 4) {
      pages.push('...')
    }
    const start = Math.max(2, current - 2)
    const end = Math.min(total - 1, current + 2)
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    if (current < total - 3) {
      pages.push('...')
    }
    pages.push(total)
  }
  return pages
})

const handleSearch = () => {
  currentPage.value = 1
}

const resetSearch = () => {
  Object.keys(searchForm).forEach(key => searchForm[key] = '')
  currentPage.value = 1
}

const handleSort = (column) => {
  if (sortColumn.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortColumn.value = column
    sortOrder.value = 'asc'
  }
}

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++
}

const goToPage = (page) => {
  if (typeof page === 'number' && page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const handleJump = () => {
  const page = parseInt(jumpPage.value)
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  } else {
    jumpPage.value = currentPage.value
  }
}

const toggleColumnSelector = () => {
  showColumnSelector.value = !showColumnSelector.value
}

const updateVisibleColumns = () => {
  const visibleCount = allColumns.value.filter(col => col.visible).length
  if (visibleCount < 1) {
    const firstHidden = allColumns.value.find(col => !col.visible)
    if (firstHidden) firstHidden.visible = true
  }
}

const resetColumns = () => {
  allColumns.value.forEach(col => col.visible = true)
}

const closeModal = () => {
  showAddModal.value = false
  editingUser.value = null
  isView.value = false
  selectedTenantIds.value = []
}

const openAddModal = () => {
  editingUser.value = null
  isView.value = false
  selectedTenantIds.value = []
  form.account = ''
  form.name = ''
  form.gender = '男'
  form.position = ''
  form.email = ''
  form.phone = ''
  form.employeeNo = ''
  form.leader = ''
  form.leaderName = ''
  form.userType = '普通用户'
  form.canLogin = true
  form.password = ''
  form.confirmPassword = ''
  form.organization = ''
  form.roleNames = ''
  form.tenants = []
  selectedRoles.value = []
  selectedRoleIds.value = []
  selectedOrgIds.value = []
  oldOrgIds.value = []
  oldRoleIds.value = []
  showAddModal.value = true
}

const ensureArray = (val) => {
  if (Array.isArray(val)) return val
  if (!val) return []
  if (typeof val === 'string') return val.split(',').filter(Boolean).map(s => ({ guid: s.trim(), name: s.trim() }))
  return []
}

const editUser = (user) => {
  isView.value = false
  editingUser.value = user
  form.account = user.account
  form.name = user.name
  form.gender = user.gender
  form.position = user.position
  form.email = user.email
  form.phone = user.phone
  form.employeeNo = user.employeeNo
  form.leader = user.leader
  form.leaderName = user.leaderName
  form.userType = user.userType
  form.canLogin = user.userStatus === 0
  form.password = ''
  form.confirmPassword = ''
  form.organization = user.organization || ''
  form.roleNames = user.roles || ''
  form.tenants = user.tenants
  selectedTenantIds.value = ensureArray(user.tenants).map(t => t.guid || t.Guid)

  // 根据用户已有的组织/角色名称，匹配树节点找到 GUID
  const orgNames = (user.organization || '').split(',').map(s => s.trim()).filter(Boolean)
  selectedOrgIds.value = findIdsByNames(orgTree.value, orgNames)
  oldOrgIds.value = [...selectedOrgIds.value]

  const roleNamesArr = (user.roles || '').split(',').map(s => s.trim()).filter(Boolean)
  const roleGuids = findIdsByNames(roleTree.value, roleNamesArr)
  selectedRoles.value = roleGuids
  selectedRoleIds.value = [...roleGuids]
  oldRoleIds.value = [...roleGuids]

  showAddModal.value = true
}

// 根据名称在树中查找节点 ID
const findIdsByNames = (tree, names) => {
  const result = []
  const walk = (nodes) => {
    for (const n of nodes) {
      if (names.includes(n.name)) result.push(n.id)
      if (n.children && n.children.length > 0) walk(n.children)
    }
  }
  walk(tree)
  return result
}

const viewUser = (user) => {
  isView.value = true
  editingUser.value = user
  form.account = user.account
  form.name = user.name
  form.gender = user.gender
  form.position = user.position
  form.email = user.email
  form.phone = user.phone
  form.employeeNo = user.employeeNo
  form.leader = user.leader
  form.leaderName = user.leaderName
  form.userType = user.userType
  form.canLogin = user.userStatus === 0
  form.password = ''
  form.confirmPassword = ''
  form.organization = user.organization || ''
  form.roleNames = user.roles || ''
  form.tenants = user.tenants
  showAddModal.value = true
}

// 显示确认弹窗
const showConfirm = (title, message, type = 'primary', text = '确认') => {
  confirmTitle.value = title
  confirmMessage.value = message
  confirmType.value = type
  confirmText.value = text
  confirmVisible.value = true
}

const handleConfirm = async () => {
  if (pendingAction) {
    confirmLoading.value = true
    try {
      await pendingAction()
    } catch (e) {
      $message.error('操作失败：' + (e.response?.data?.message || e.message))
    } finally {
      confirmLoading.value = false
      confirmVisible.value = false
      pendingAction = null
    }
  }
}

const handleConfirmCancel = () => {
  confirmVisible.value = false
  pendingAction = null
}

// 失效用户
const disableUserHandle = (user) => {
  showConfirm('确认失效', `确定要失效用户「${user.name}(${user.account})」吗？`, 'danger')
  pendingAction = async () => {
    await disableUser(user.guid)
    await loadUsers()
  }
}

// 启用用户
const enableUserHandle = (user) => {
  showConfirm('确认启用', `确定要启用用户「${user.name}(${user.account})」吗？`, 'primary')
  pendingAction = async () => {
    await enableUser(user.guid)
    await loadUsers()
  }
}

const saveUser = async () => {
  if (!form.account || !form.name) {
    $message.warning('请填写必填字段')
    return
  }
  if (!editingUser.value && (!form.password || form.password !== form.confirmPassword)) {
    $message.warning('密码不能为空且两次输入必须一致')
    return
  }

  // 组装后端 SYSUser 实体（逐字段构建，避免 camelCase/PascalCase 冲突）
  const raw = (editingUser.value && editingUser.value._raw) || {}
  // 用户类型 → CostCenter 映射：供应商=1, 客户=2, 管理员/普通用户=0
  let costCenter = '0'
  let isSuper = 0
  if (form.userType === '供应商') { costCenter = '1'; isSuper = 0 }
  else if (form.userType === '客户') { costCenter = '2'; isSuper = 0 }
  else if (form.userType === '管理员') { costCenter = '0'; isSuper = 1 }
  else { costCenter = '0'; isSuper = 0 }

  // canLogin: true → UserStatus=0, false → UserStatus=1
  const userStatus = form.canLogin ? 0 : 1

  const payload = {
    Guid: (editingUser.value && (editingUser.value.guid)) || '',
    Account: form.account,
    DisplayName: form.name,
    FirstName: raw.firstName || raw.FirstName || '',
    LastName: raw.lastName || raw.LastName || '',
    ChName: raw.chName || raw.ChName || form.name,
    Sex: form.gender === '男' ? 1 : 0,
    Post: form.position,
    Email: form.email,
    Tel: raw.tel || raw.Tel || '',
    Mb: form.phone,
    DeptGuid: raw.deptGuid || raw.DeptGuid || '',
    HRID: form.employeeNo,
    SuperManage: form.leader,
    IsSuperUser: isSuper,
    CostCenter: costCenter,
    UserStatus: userStatus,
    Active: 1,
    TenantIds: selectedTenantIds.value.length > 0 ? [...selectedTenantIds.value] : null,
    Creater: raw.creater || raw.Creater || '',
  }
  console.log('[Users] saveUser payload:', JSON.stringify(payload, null, 2))
  console.log('[Users] saveUser payload.HRID =', payload.HRID, 'form.employeeNo =', JSON.stringify(form.employeeNo))

  try {
    await saveUserApi(payload)

    // 同步组织成员关系：计算差异，删旧加新
    const account = form.account
    const userGuid = (editingUser.value && editingUser.value.guid) || payload.Guid

    if (editingUser.value) {
      const toRemove = oldOrgIds.value.filter(id => !selectedOrgIds.value.includes(id))
      const toAdd = selectedOrgIds.value.filter(id => !oldOrgIds.value.includes(id))
      for (const orgId of toRemove) {
        try { await removeOrgUser(orgId, account) } catch (e) { console.warn('移除组织成员失败', orgId, e) }
      }
      for (const orgId of toAdd) {
        try {
          await addOrgUser(orgId, { Guid: userGuid, Account: account, DisplayName: form.name })
        } catch (e) { console.warn('添加组织成员失败', orgId, e) }
      }
    } else {
      // 新增用户：添加所有选中组织
      for (const orgId of selectedOrgIds.value) {
        try {
          await addOrgUser(orgId, { Guid: userGuid, Account: account, DisplayName: form.name })
        } catch (e) { console.warn('添加新用户组织失败', orgId, e) }
      }
    }

    // 同步角色成员关系：计算差异，按 Account 删旧加新
    if (editingUser.value) {
      const toRemove = oldRoleIds.value.filter(id => !selectedRoleIds.value.includes(id))
      const toAdd = selectedRoleIds.value.filter(id => !oldRoleIds.value.includes(id))
      for (const roleId of toRemove) {
        try { await removeRoleUser(roleId, account) } catch (e) { console.warn('移除角色成员失败', roleId, e) }
      }
      for (const roleId of toAdd) {
        try {
          await addRoleUsers(roleId, [{ Guid: userGuid, Account: account, DisplayName: form.name }])
        } catch (e) { console.warn('添加角色成员失败', roleId, e) }
      }
    } else if (selectedRoleIds.value.length > 0) {
      // 新增用户：添加所有选中角色
      for (const roleId of selectedRoleIds.value) {
        try {
          await addRoleUsers(roleId, [{ Guid: userGuid, Account: account, DisplayName: form.name }])
        } catch (e) { console.warn('添加新用户角色失败', roleId, e) }
      }
    }

    closeModal()
    await loadUsers()
  } catch (e) {
    $message.error('保存失败：' + ((e.response && e.response.data && (e.response.data.message || e.response.data.Message)) || e.message))
  }
}

const closePasswordModal = () => {
  showPasswordModal.value = false
  editingUser.value = null
  passwordForm.newPassword = ''
  passwordForm.confirmPassword = ''
}

const updatePassword = async () => {
  if (!passwordForm.newPassword || passwordForm.newPassword !== passwordForm.confirmPassword) {
    $message.warning('密码不能为空且两次输入必须一致')
    return
  }
  try {
    await updateUserPassword(editingUser.value.guid, '', passwordForm.newPassword)
    closePasswordModal()
    $message.success('密码修改成功')
  } catch (e) {
    $message.error('修改密码失败：' + (e.response?.data?.message || e.message))
  }
}

const resetPassword = (user) => {
  editingUser.value = user
  passwordForm.newPassword = ''
  passwordForm.confirmPassword = ''
  showPasswordModal.value = true
}

const onOrgSelected = () => {
  const orgNames = organizations.value.filter(o => selectedOrgIds.value.includes(o.id)).map(o => o.name)
  form.organization = orgNames.join(', ')
  form.organizationId = selectedOrgIds.value.join(',')
  showOrgSelector.value = false
}

const onLeaderSelected = (item) => {
  // PersonSelector 返回 { type, code, name }
  form.leaderName = item.name || item.code
  form.leader = item.code || ''
  showLeaderSelector.value = false
}

const confirmRoles = () => {
  const selectedNames = roles.value.filter(r => selectedRoleIds.value.includes(r.id)).map(r => r.name)
  form.roleNames = selectedNames.join(', ')
  selectedRoles.value = [...selectedRoleIds.value]
  showRoleSelector.value = false
}

// 账套选择相关
const tenantDisplayText = computed(() => {
  if (selectedTenantIds.value.length === 0) return ''
  const names = allTenants.value.filter(t => selectedTenantIds.value.includes(t.id)).map(t => t.name)
  return names.join(', ')
})

const filteredTenants = computed(() => {
  if (!tenantSearch.value) return allTenants.value
  const kw = tenantSearch.value.toLowerCase()
  return allTenants.value.filter(t => t.name.toLowerCase().includes(kw) || t.tenantID.toLowerCase().includes(kw))
})

const isAllTenantSelected = computed(() =>
  filteredTenants.value.length > 0 && filteredTenants.value.every(t => selectedTenantIds.value.includes(t.id))
)

const toggleTenant = (id) => {
  const idx = selectedTenantIds.value.indexOf(id)
  if (idx === -1) selectedTenantIds.value.push(id)
  else selectedTenantIds.value.splice(idx, 1)
}

const toggleAllTenant = () => {
  if (isAllTenantSelected.value) {
    // 取消全选当前筛选项
    const ids = filteredTenants.value.map(t => t.id)
    selectedTenantIds.value = selectedTenantIds.value.filter(id => !ids.includes(id))
  } else {
    filteredTenants.value.forEach(t => {
      if (!selectedTenantIds.value.includes(t.id)) selectedTenantIds.value.push(t.id)
    })
  }
}

const confirmTenants = () => {
  showTenantSelector.value = false
}

const dragStart = (event, column) => {
  event.dataTransfer.setData('column', column.key)
}

const dropColumn = (event, targetColumn) => {
  const draggedKey = event.dataTransfer.getData('column')
  if (draggedKey === targetColumn.key) return
  
  const draggedIndex = allColumns.value.findIndex(c => c.key === draggedKey)
  const targetIndex = allColumns.value.findIndex(c => c.key === targetColumn.key)
  
  if (draggedIndex !== -1 && targetIndex !== -1) {
    const [dragged] = allColumns.value.splice(draggedIndex, 1)
    allColumns.value.splice(targetIndex, 0, dragged)
  }
}
</script>

<style scoped>
.view-container {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 150px);
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e7eaec;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #3a4a63;
  margin: 0;
}

.btn {
  padding: 6px 15px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: #1ab394;
  color: #fff;
}

.btn-primary:hover {
  background-color: #18a689;
}

.btn-default {
  background-color: #f3f3f4;
  color: #676a6c;
  border: 1px solid #e7eaec;
}

.btn-default:hover {
  background-color: #e7eaec;
}

.btn-info {
  background-color: #31b0d5;
  color: #fff;
}

.btn-info:hover {
  background-color: #269abc;
}

.btn-warning {
  background-color: #f0ad4e;
  color: #fff;
}

.btn-warning:hover {
  background-color: #ec971f;
}

.btn-danger {
  background-color: #d9534f;
  color: #fff;
}

.btn-danger:hover {
  background-color: #c9302c;
}

.btn-success {
  background-color: #5cb85c;
  color: #fff;
}

.btn-success:hover {
  background-color: #4cae4c;
}

.btn-xs {
  padding: 3px 8px;
  font-size: 12px;
  margin-right: 3px;
}

.btn-sm {
  padding: 5px 12px;
  font-size: 12px;
}

.search-panel {
  background: #fff;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}

.search-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.search-item {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.search-item label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.search-item label i {
  margin-right: 2px;
  color: #1ab394;
  font-size: 12px;
}

.search-input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  width: 160px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fafbfc;
}

.search-input:focus {
  border-color: #1ab394;
  box-shadow: 0 0 0 3px rgba(26,179,148,0.12);
  background: #fff;
}

.search-select {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  width: 130px;
  outline: none;
  background: #fafbfc;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  appearance: auto;
}

.search-select:focus {
  border-color: #1ab394;
  box-shadow: 0 0 0 3px rgba(26,179,148,0.12);
  background: #fff;
}

.search-actions {
  display: flex;
  gap: 10px;
  margin-left: auto;
  align-self: flex-end;
}

.search-btn {
  padding: 8px 18px;
  font-size: 13px;
  border-radius: 6px;
  font-weight: 500;
}

.table-wrapper {
  border: 1px solid #e7eaec;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.table-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #e7eaec;
  flex-shrink: 0;
}

.column-settings-btn {
  padding: 4px 10px;
  font-size: 12px;
}

.table-container {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.data-table th,
.data-table td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-table thead,
.data-table tbody {
  display: table-row-group;
}

.data-table tr {
  display: table-row;
}

.data-table th,
.data-table td {
  display: table-cell;
  padding: 10px 15px;
  text-align: left;
  border-bottom: 1px solid #e7eaec;
  white-space: nowrap;
}

.data-table th {
  background-color: #f5f5f5;
  font-weight: 600;
  color: #676a6c;
  font-size: 13px;
  position: sticky;
  top: 0;
  z-index: 2;
}

.data-table td {
  font-size: 13px;
  color: #676a6c;
}

.column-header {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 5px;
}

.column-header:hover {
  background-color: #e7eaec;
}

.sortable {
  padding-right: 25px;
}

.sorted-asc,
.sorted-desc {
  background-color: #e8f5f3;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.valid {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.no-login {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge.disabled {
  background-color: #f8d7da;
  color: #721c24;
}

.tenant-tag {
  display: inline-block;
  padding: 2px 10px;
  background: #e8f5f3;
  color: #1ab394;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.action-column {
  white-space: nowrap;
  width: 70px;
}

.pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background-color: #f9f9f9;
  border-top: 1px solid #e7eaec;
  flex-shrink: 0;
}

.pagination-info {
  font-size: 13px;
  color: #676a6c;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-info {
  font-size: 13px;
  color: #676a6c;
}

.page-numbers {
  display: flex;
  gap: 5px;
}

.page-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e7eaec;
  background-color: #fff;
  color: #676a6c;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.page-btn:hover:not(.disabled) {
  background-color: #f9f9f9;
  border-color: #1ab394;
  color: #1ab394;
}

.page-btn.active {
  background-color: #1ab394;
  border-color: #1ab394;
  color: #fff;
}

.page-btn.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.pagination-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-right: 10px;
  padding-right: 10px;
  border-right: 1px solid #e7eaec;
}

.page-size-selector span {
  font-size: 13px;
  color: #676a6c;
}

.page-size-input {
  width: 60px;
  padding: 5px 8px;
  font-size: 13px;
}

.page-jump {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: 10px;
  padding-left: 10px;
  border-left: 1px solid #e7eaec;
}

.page-jump span {
  font-size: 13px;
  color: #676a6c;
}

.jump-input {
  width: 50px;
  padding: 5px 8px;
  border: 1px solid #e7eaec;
  border-radius: 4px;
  font-size: 13px;
  text-align: center;
}

.jump-input:focus {
  outline: none;
  border-color: #1ab394;
  box-shadow: 0 0 0 2px rgba(26, 179, 148, 0.2);
}

.jump-btn {
  padding: 5px 12px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background-color: #fff;
  border-radius: 6px;
  width: 90%;
  max-width: 750px;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e7eaec;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #3a4a63;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #676a6c;
  font-size: 16px;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
  max-height: calc(90vh - 140px);
  overflow-y: auto;
  overflow-x: hidden;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #e7eaec;
}

.form-horizontal {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-row {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.form-group {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 180px;
  max-width: 250px;
}

.form-group label {
  font-size: 13px;
  color: #676a6c;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group .form-control {
  padding: 8px 12px;
  border: 1px solid #e7eaec;
  border-radius: 4px;
  font-size: 13px;
}

.form-group .form-control:disabled {
  background-color: #f3f3f4;
  cursor: not-allowed;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
}

.search-select {
  position: relative;
  width: 100%;
}

.search-select .search-btn {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 32px;
  background: none;
  border: 1px solid #e7eaec;
  border-left: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  color: #676a6c;
}

.search-select .form-control {
  padding-right: 35px;
  width: 100%;
  min-width: 120px;
  max-width: 200px;
}

.column-selector-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.column-selector-content {
  background-color: #fff;
  border-radius: 6px;
  width: 350px;
}

.column-selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e7eaec;
}

.column-selector-header h4 {
  margin: 0;
  font-size: 14px;
}

.column-selector-body {
  padding: 15px;
  max-height: 300px;
  overflow-y: auto;
}

.column-checkbox {
  display: block;
  padding: 8px 10px;
  cursor: pointer;
  border-radius: 4px;
}

.column-checkbox:hover {
  background-color: #f5f5f5;
}

.column-checkbox input {
  margin-right: 10px;
}

.column-selector-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px;
  border-top: 1px solid #e7eaec;
}

.search-box {
  margin-bottom: 15px;
}

.search-box .form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e7eaec;
  border-radius: 4px;
}

.role-list {
  max-height: 250px;
  overflow-y: auto;
}

.role-checkbox {
  display: block;
  padding: 10px 15px;
  cursor: pointer;
  border-radius: 4px;
}

.role-checkbox:hover {
  background-color: #f5f5f5;
}

.role-checkbox input {
  margin-right: 10px;
}

.select-all-row {
  display: block;
  padding: 10px 15px;
  cursor: pointer;
  border-radius: 4px;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
}

.empty-tip {
  padding: 20px;
  text-align: center;
  color: #9ca3af;
}

@media (max-width: 768px) {
  .search-row {
    flex-wrap: wrap;
  }
  
  .form-row {
    flex-direction: column;
  }
}
</style>
